import TimeTextInput from '../ui/TimeTextInput';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoonStar } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import Dialog from '../ui/Dialog';
import { apiErrorMessage } from '../TimerSummaryModal';
import { formatDate } from '../../utils/format';
import type { AutoCloseout } from '../../types';
import { changeBody, clockOutHhmm, closeoutDayIsYesterday, hhmm, nextToAsk, readLater, writeLater } from './autoCloseoutModel';
import { localDateKey } from '../home/homeModel';

const sessionStore = (): Storage | null => {
  try { return window.sessionStorage; } catch { return null; }
};

/**
 * One day closed at 00:00: what happened, the suggested time and the three
 * answers — [לאשר] (the suggested times), [לשנות] (the clock-out time and each
 * stopped timer's end), [אחר כך]. Used by the morning notice and the
 * attendance page (to update later).
 */
export function AutoCloseoutBody({ closeout, onDone, onLater, compact = false }: {
  closeout: AutoCloseout;
  onDone: () => void;
  onLater?: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [clockOut, setClockOut] = useState(() => hhmm(closeout.clock_out_at && closeout.status !== 'pending' ? closeout.clock_out_at : closeout.suggested_at) || '');
  const [ends, setEnds] = useState<Record<string, string>>(() => Object.fromEntries(
    closeout.timers.filter((tm) => tm.work_log_id).map((tm) => [tm.work_log_id as string, hhmm(tm.suggested_end) || hhmm(tm.end_time)]),
  ));

  const resolve = useMutation({
    mutationFn: (body: Parameters<typeof api.resolveAutoCloseout>[1]) => api.resolveAutoCloseout(closeout.id, body),
    onSuccess: () => {
      toast('success', t('autoCloseout.saved'));
      onDone();
    },
    onError: (err) => toast('error', apiErrorMessage(err) || t('autoCloseout.saveFailed')),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['auto-closeouts'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['work-logs'] });
    },
  });

  const day = formatDate(`${closeout.work_date}T00:00:00`);
  const suggested = hhmm(closeout.suggested_at);
  return (
    <div className="space-y-3 text-sm" data-testid="auto-closeout">
      <p className="text-text">
        {closeoutDayIsYesterday(closeout.work_date, localDateKey(new Date()))
          ? t('autoCloseout.happenedYesterday')
          : t('autoCloseout.happened', { date: day })}
      </p>
      {suggested
        ? <p className="text-text">{t('autoCloseout.suggest', { time: suggested })}</p>
        : <p className="text-text-2">{t('autoCloseout.noSuggestion')}</p>}
      {closeout.timers.length > 0 && (
        <ul className="space-y-1 text-text-2">
          {closeout.timers.map((tm) => (
            <li key={tm.work_log_id ?? tm.ticket_id}>
              {t('autoCloseout.timerLine', { title: tm.ticket_title ?? '', time: hhmm(tm.suggested_end) || '—' })}
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-2">{t('autoCloseout.clockOutTime')}</span>
            <TimeTextInput className="input" value={clockOut} onChange={(v) => setClockOut(v)} />
          </label>
          {closeout.timers.filter((tm) => tm.work_log_id).map((tm) => (
            <label key={tm.work_log_id} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-text-2">{t('autoCloseout.timerEnd', { title: tm.ticket_title ?? '' })}</span>
              <TimeTextInput
                className="input"
                value={ends[tm.work_log_id as string] ?? ''}
                onChange={(v) => setEnds((prev) => ({ ...prev, [tm.work_log_id as string]: v }))}
              />
            </label>
          ))}
        </div>
      )}
      <div className={`flex flex-wrap gap-2 ${compact ? '' : 'pt-1'}`}>
        {editing ? (
          <>
            <button type="button" className="btn btn-primary btn-sm" disabled={resolve.isPending}
              onClick={() => resolve.mutate(changeBody(closeout, clockOut, ends))}>
              {t('autoCloseout.save')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
          </>
        ) : (
          <>
            {suggested && (
              <button type="button" className="btn btn-primary btn-sm" disabled={resolve.isPending}
                onClick={() => resolve.mutate({ action: 'confirm' })}>
                {t('autoCloseout.confirm')}
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>{t('autoCloseout.change')}</button>
            {onLater && <button type="button" className="btn btn-ghost btn-sm" onClick={onLater}>{t('autoCloseout.later')}</button>}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * P-37 (D-194 Q-185 / Q-186) — the morning notice. Once she is clocked in, a
 * day closed automatically at 00:00 that waits for her answer is shown ONCE
 * (one window, not repeated): [אחר כך] puts it off for this session; it is
 * still on the attendance page to update any time.
 */
export default function AutoCloseoutNotice() {
  const { t } = useTranslation();
  const { data: att } = useQuery({
    queryKey: ['attendance-status'],
    queryFn: () => api.getAttendanceStatus(),
    staleTime: 30_000,
  });
  const clockedIn = att?.data?.status === 'clocked_in';
  const { data } = useQuery({
    queryKey: ['auto-closeouts', 'pending'],
    queryFn: () => api.getAutoCloseouts('pending'),
    enabled: clockedIn,
    staleTime: 60_000,
  });
  const [later, setLater] = useState<Set<string>>(() => readLater(sessionStore()));
  const current = useMemo(() => nextToAsk(data?.data ?? [], later), [data, later]);
  if (!clockedIn || !current) return null;

  const putOff = () => {
    const next = new Set(later);
    next.add(current.id);
    writeLater(sessionStore(), next);
    setLater(next);
  };
  return (
    <Dialog open onClose={putOff} title={t('autoCloseout.title')} icon={<MoonStar size={18} aria-hidden="true" />} size="md">
      <AutoCloseoutBody closeout={current} onDone={putOff} onLater={putOff} />
    </Dialog>
  );
}

/** The attendance page: the days closed at 00:00 in the last month, to update any time. */
export function AutoCloseoutList() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['auto-closeouts', 'recent'],
    queryFn: () => api.getAutoCloseouts('recent'),
    staleTime: 60_000,
  });
  const list = data?.data ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  if (list.length === 0) return null;
  return (
    <section className="rounded-xl border border-border bg-surface-0 p-4" aria-labelledby="auto-closeouts-title" data-testid="auto-closeout-list">
      <h2 id="auto-closeouts-title" className="mb-2 flex items-center gap-2 text-sm font-semibold text-text">
        <MoonStar size={16} aria-hidden="true" /> {t('autoCloseout.listTitle')}
      </h2>
      <ul className="divide-y divide-border">
        {list.map((c) => (
          <li key={c.id} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="text-text">
                {formatDate(`${c.work_date}T00:00:00`)} · {t('autoCloseout.clockOutAt', { time: clockOutHhmm(c.clock_out_at) }) /* DQ-027 */}
                {c.status === 'pending' && <span className="ms-2 text-xs font-medium text-warning-800">{t('autoCloseout.pending')}</span>}
              </span>
              <button type="button" className="text-xs font-medium text-accent-text hover:underline underline-offset-2"
                onClick={() => setOpenId(openId === c.id ? null : c.id)} aria-expanded={openId === c.id}>
                {t('autoCloseout.update')}
              </button>
            </div>
            {openId === c.id && (
              <div className="mt-2"><AutoCloseoutBody compact closeout={c} onDone={() => setOpenId(null)} /></div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
